import { Injectable } from '@angular/core';
import { CanActivate, ActivatedRouteSnapshot, RouterStateSnapshot, Router, UrlTree } from '@angular/router';

import { AccountService, AccountErrorCodes } from 'src/app/services/account/account.service';
import { OrganizationMemberService } from 'src/app/services/organization-member/organization-member.service';
import { UserService } from 'src/app/services/user/user.service';
import { AuthBridgeService } from 'src/app/services/auth-bridge.service';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class RoleGuard implements CanActivate {
  constructor(
    private router: Router,
    private account: AccountService,
    private organizationMemberService: OrganizationMemberService,
    private user: UserService,
    private authBridge: AuthBridgeService,
  ) {}

  async canActivate(next: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean | UrlTree> {
    // canActivate guards run concurrently with AuthGuard, which is what
    // establishes the centralized session. Await the shared exchange first so
    // currentUser is populated before we read it (else "User not found").
    await this.authBridge.ensureReady();

    // getCurrentUserDocument throws (not returns null) when there's no signed-in
    // user. If the session genuinely isn't there, defer to /login, which hands
    // off to centralized auth — don't let the throw break navigation (AuthGuard,
    // running concurrently, redirects to /login too).
    let currentUser;
    try {
      currentUser = await this.user.getCurrentUserDocument();
    } catch {
      return this.router.parseUrl('/login');
    }
    if (!currentUser) {
      return this.router.parseUrl('/login');
    }

    let currentAccount;
    try {
      currentAccount = await this.account.getCurrentAccount();
    } catch (e: any) {
      // User has no organization — redirect to admin console to create/join one
      window.location.href = environment.adminConsoleUrl + '/new';
      return false;
    }

    const organizationId = currentAccount?.organizationId;
    if (!organizationId) {
      window.location.href = environment.adminConsoleUrl + '/new';
      return false;
    }

    try {
      await this.organizationMemberService.getCurrentMember(organizationId);
      return true;
    } catch {
      window.location.href = environment.adminConsoleUrl + '/new';
      return false;
    }
  }
}
